// Dashboard-Widget: Durchlaufzeit-KPI je Modul
// A1 Feature — zeigt n, Ø, P50, P90 für letzte 90 Tage.
// Bei keine_sauberen_daten: Hinweis-Banner, Fallback auf All-Werte (grau).

import { useEffect, useState } from 'react';
import { api, ApiError, type DurchlaufzeitOverview } from '../lib/api';
import { Clock, TrendingUp, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function DurchlaufzeitWidget() {
  const [data, setData] = useState<DurchlaufzeitOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.statistik
      .durchlaufzeitOverview(90)
      .then((d) => {
        if (mounted) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (mounted) {
          setError(e instanceof ApiError ? `${e.status}` : 'Fehler');
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
        <div className="text-sm text-slate-400">Durchlaufzeit lädt…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
        <div className="text-sm text-red-600">Fehler beim Laden ({error})</div>
      </div>
    );
  }
  if (!data) return null;

  const hasClean = data.clean_records > 0;

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-brand" />
          <h3 className="text-sm font-semibold text-slate-700">
            Durchlaufzeit <span className="text-slate-400 font-normal">90 Tage</span>
          </h3>
        </div>
        <Link
          to="/statistik"
          className="text-xs text-brand hover:underline inline-flex items-center gap-1"
        >
          Details <TrendingUp className="w-3 h-3" />
        </Link>
      </div>

      {data.note && (
        <div className="mb-3 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2 rounded">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{data.note}</span>
        </div>
      )}

      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-500 border-b border-slate-100">
            <th className="text-left py-1 font-medium">Modul</th>
            <th className="text-right py-1 font-medium">n</th>
            <th className="text-right py-1 font-medium">Ø Tage</th>
            <th className="text-right py-1 font-medium">P50</th>
            <th className="text-right py-1 font-medium">P90</th>
          </tr>
        </thead>
        <tbody>
          {data.modules.map((m) => {
            const metric = hasClean && m.clean.count > 0 ? m.clean : m.all;
            const isDirty = !hasClean || m.clean.count === 0;
            return (
              <tr
                key={m.modul}
                className="border-b border-slate-50 last:border-0"
                title={isDirty ? 'Basis: Legacy/Cleanup-Daten — noch keine sauberen Ereignisse' : 'Saubere Daten'}
              >
                <td className="py-1.5 capitalize text-slate-700">{m.modul}</td>
                <td className={`py-1.5 text-right tabular-nums ${isDirty ? 'text-slate-400' : ''}`}>
                  {metric.count}
                </td>
                <td
                  className={`py-1.5 text-right tabular-nums font-semibold ${
                    isDirty ? 'text-slate-400' : 'text-slate-800'
                  }`}
                >
                  {metric.count ? metric.avg_kalender : '—'}
                </td>
                <td className={`py-1.5 text-right tabular-nums ${isDirty ? 'text-slate-400' : 'text-slate-600'}`}>
                  {metric.count ? metric.p50_kalender : '—'}
                </td>
                <td className={`py-1.5 text-right tabular-nums ${isDirty ? 'text-slate-400' : 'text-slate-600'}`}>
                  {metric.count ? metric.p90_kalender : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!hasClean && (
        <div className="mt-2 text-[11px] text-slate-400">
          Anzeige aus Legacy-Daten. Grau = unzuverlässig. Neue Fertigmeldungen werden sauber erfasst.
        </div>
      )}
    </div>
  );
}
